
import Vue from 'vue'
import i18n from '../i18n'

import UiCard from '@/components/UiCard.vue'
import UiButton from '@/components/UiButton.vue'
import UiChip from '@/components/UiChip.vue'
import UiSkeleton from '@/components/UiSkeleton.vue'
import UiIcon from '@/components/UiIcon.vue'
import AuthorInput from '@/components/AuthorInput.vue'
import FeedFilters from '@/components/FeedFilters.vue'
import AppFooter from '@/components/AppFooter.vue'
import AppHeader from '@/components/AppHeader.vue'
import PostGallery from '@/components/PostGallery.vue'
import PostPdfs from '@/components/PostPdfs.vue'
import PostTweets from '@/components/PostTweets.vue'
import PostYoutubes from '@/components/PostYoutubes.vue'
import PostText from '@/components/PostText.vue'

import {flattenedAttachmentsWithoutParent} from '@/services/flattenAttachments'

import {LabelInterface, PostInterface} from '@/types/interfaces'

import moment from 'moment'

export default Vue.extend({
  name: 'Feed',
  data(){
    return {
      loadedPosts: false,
      loadingMorePosts: false,
      navHeaderHeight: 45,
      navFilterHeaderHeight: 97,
      filterLabels: [] as LabelInterface[],
      filter: {
        labelIds: [] as string[],
        releasedAtStart: "",
        releasedAtEnd: "",
      },
      isAddFilterFocus: false,
      labelFilterInputEl: null as any,
      startDateFilterDraft: "",
      endDateFilterDraft: "",
    }
  },
  computed: {
    organization(){ return this.$store.getters.organization },
    organizationlabels(){ return this.$store.getters.organizationLabels },
    posts(){ return this.$store.getters.posts },
    areAllPostsLoaded(){ return this.$store.getters.loadedAllPosts },
    isAuthorOrAdmin(){ return this.$store.getters.userIsAuthorOrAdmin }
  },
  methods: {
    organizationLabel(id: string){
      return this.organizationlabels[id]
    },
    labelImg(id: string){
      return this.organizationLabel(id) ? this.organizationLabel(id).img : null
    },
    labelValue(id: string){
      return this.organizationLabel(id) ? this.organizationLabel(id).value : null
    },
    flattenedAttachmentPreviews(post: PostInterface){
      return flattenedAttachmentsWithoutParent(post.attachments)
    },
    attachedPDFs(post: PostInterface){
      return post.attachments
        .filter(attachment => attachment.type === "pdf")
    },
    attachedSocialTweets(post: PostInterface){
      return post.socialAttachments ? post.socialAttachments.map(a => a.reference) : []
    },
    socialAttachmentsWithIndex(post: PostInterface, platform: string){
      return post.socialAttachments ? post.socialAttachments.map((a, i) => {
        const attachmentWithIndex = Object.assign({}, a)
        attachmentWithIndex.index = i
        return attachmentWithIndex
      }).filter(a => a.platform == platform) : []
    },
    formatDate(date: Date){
      return moment(date).format("ddd, D. MMM YYYY") // TODO: Edit based on locale
    },
    formatFilterDate(date: Date){
      return moment(date).format("D. MMM YYYY") // TODO: Edit based on locale
    },
    formatTime(date: Date){
      return moment(date).format("HH:mm")
    },
    formatDateTime(date: Date){
      return moment(date).format("D. MMM. YY, HH:mm")
    },
    async handleReloadPosts(){
      return await this._loadPosts()
    },
    async handleLoadMorePosts(){
      this.$data.loadingMorePosts = true

      const releasedAtStart = this.filter.releasedAtStart ? new Date(this.filter.releasedAtStart) : null
      const releasedAtEnd = this.filter.releasedAtEnd ? this._setMaxDateTodayForReaders(new Date(this.filter.releasedAtEnd)) : null

      await this.$store.dispatch('getPosts', {
        isLoadMore: true,
        filter: {
          labelIds: this.filter.labelIds,
          releasedAtStart,
          releasedAtEnd,
        },
      })
      this.$data.loadingMorePosts = false
    },
    async handleDeletePost(post: PostInterface){
      if(!confirm( this.$t('confirmDeletePost').toString() )) return
      await this.$store.dispatch('deletePost', post)
    },
    isFirstOfDay(postIndex: number){
      // Display date for first post
      if(postIndex == 0) return true 
      // Display date for posts that have a different date than the day before:
      return this.formatDate(this.posts[postIndex].releasedAt) != this.formatDate(this.posts[postIndex - 1].releasedAt)
    },
    isDateFuture(date: Date){
      return date > (this.$store.getters.serverTime || new Date())
    },
    serverTime(){ return this.$store.getters.serverTime || new Date() },
    async _loadPosts(){
      this.$data.loadedPosts = false

      const releasedAtStart = this.filter.releasedAtStart ? moment(this.filter.releasedAtStart).startOf("day").toDate() : null
      const releasedAtEnd = this.filter.releasedAtEnd ? this._setMaxDateTodayForReaders(
        moment(
          this._setMaxDateTodayForReaders(new Date(this.filter.releasedAtEnd))
        ).endOf("day").toDate()
      ) : null
      
      await this.$store.dispatch('getPosts', {
        isLoadMore: false,
        filter: {
          labelIds: this.filter.labelIds,
          releasedAtStart,
          releasedAtEnd,
        },
      })
      this.$data.loadedPosts = true
    },
    labelIdsFromQuery(query : string | Array<string | null>) : Array<string> {
      if(!query) return []
      return ([] as Array<string | null>).concat(query).filter(l => typeof l != null) as string[]
    },
    currentLabelIds(){ return this.labelIdsFromQuery(this.$route.query.labelIds) },
    isFiltered(){ return Object.values(this.filter).some(f => f.length > 0) },
    _labelIdToObj(id: string){
      const organizationLabelObj = this.$store.getters.organization.labels[id]
      return organizationLabelObj || null
    },
    _labelIdsToObjs(ids: Array<string>){
      return ids.map(id => this._labelIdToObj(id)).filter(label => label != null)
    },
    _setMaxDateTodayForReaders(endDate : Date){
      if(this.$store.getters.userIsAuthorOrAdmin) return endDate

      return (endDate > this.$store.getters.serverTime) ? this.$store.getters.serverTime : endDate
    },
  },
  async mounted(){
    // Set locale
    moment.locale(i18n.locale)
    // Set filters from query
    this.filter.labelIds = this.labelIdsFromQuery(this.$route.query.labelIds)
    this.filter.releasedAtStart = (this.$route.query.releasedAtStart as string) || ""
    this.filter.releasedAtEnd = (this.$route.query.releasedAtEnd as string) || ""

    this.filterLabels = this._labelIdsToObjs(this.filter.labelIds)

    // Update server time
    await this.$store.dispatch('setServerTime')
    // Load posts
    this._loadPosts()
  },
  async beforeRouteUpdate(to, from, next){ // Reload when route changes without mount
    // Set filters from query
    this.filter.labelIds = this.labelIdsFromQuery(to.query.labelIds)
    this.filter.releasedAtStart = (to.query.releasedAtStart as string) || ""
    this.filter.releasedAtEnd = (to.query.releasedAtEnd as string) || ""

    this.filterLabels = this._labelIdsToObjs(this.filter.labelIds)

    // Update server time
    await this.$store.dispatch('setServerTime')
    // Load posts
    await this._loadPosts()
    // Scroll to top: 
    const $el = document.querySelector('.topline')
    if ($el && $el.scrollIntoView) $el.scrollIntoView({behavior: 'smooth'})
    next()
  },
  components: {
    AuthorInput,
    FeedFilters,
    AppHeader,
    AppFooter,
    UiCard,
    UiButton,
    UiChip,
    UiSkeleton,
    UiIcon,
    PostGallery,
    PostPdfs,
    PostTweets,
    PostYoutubes,
    PostText,
  }
})
